import React from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  HStack,
  VStack,
  Link as ChakraLink,
  Icon,
  Image,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  useBreakpointValue,
  Divider,
} from '@chakra-ui/react';
import { ChevronDownIcon, HamburgerIcon } from '@chakra-ui/icons';
import { FiUser, FiUsers, FiCalendar, FiHome, FiLogOut } from 'react-icons/fi';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { FaTrophy } from 'react-icons/fa';

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const handleLogout = async () => {
    try {
      await logout();
      onClose(); // Close mobile menu after logout
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const handleSignIn = () => {
    onClose(); // Close mobile menu
    // Don't redirect back to auth page or landing page
    if (location.pathname === '/auth' || location.pathname === '/') {
      navigate('/auth');
    } else {
      // Preserve current page for post-auth redirect
      const returnUrl = encodeURIComponent(location.pathname);
      navigate(`/auth?returnUrl=${returnUrl}`);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    onClose(); // Close mobile menu after navigation
  };

  const navItems = [
    { label: 'Events', path: '/events', icon: FiHome },
    // TODO: Re-enable after beta testing with 1-2 live events
    // { label: 'Fighters', path: '/fighters', icon: FiUsers },
    // { label: 'History', path: '/history', icon: FiCalendar },
    { label: 'Leaderboard', path: '/leaderboard', icon: FaTrophy },
  ];

  const MobileNavItem = ({ item, isAdmin = false }) => (
    <ChakraLink
      as={RouterLink}
      to={item.path}
      onClick={() => handleNavigation(item.path)}
      color={isAdmin ? "green.300" : "gray.300"}
      _hover={{ color: isAdmin ? 'green.200' : 'white' }}
      fontSize="md"
      fontWeight="medium"
      w="full"
      p={3}
      borderRadius="md"
      _activeLink={{ bg: 'dark.700', color: 'white' }}
    >
      <HStack spacing={3}>
        <Icon as={item.icon} boxSize={5} />
        <Text>{item.label}</Text>
      </HStack>
    </ChakraLink>
  );

  return (
    <>
      <Box
        bg="dark.800"
        borderBottom="1px"
        borderColor="dark.700"
        px={4}
        position="sticky"
        top={0}
        zIndex={1000}
        boxShadow="sm"
      >
        <Flex h={16} alignItems="center" justifyContent="space-between">
          {/* Logo */}
          <ChakraLink as={RouterLink} to={user?.isAdmin ? "/admin" : "/"}>
            <Image src="/logo.png" alt="Fighter Bet Plus" height={8} />
          </ChakraLink>

          {/* Desktop Navigation */}
          {!isMobile && user && (
            <HStack spacing={6}>
              {user.isAdmin && (
                <ChakraLink
                  as={RouterLink}
                  to="/admin"
                  color="green.300"
                  _hover={{ color: 'green.200' }}
                  fontSize="sm"
                  fontWeight="medium"
                >
                  <HStack spacing={2}>
                    <Icon as={FiUser} boxSize={4} />
                    <Text>Admin</Text>
                  </HStack>
                </ChakraLink>
              )}
              {navItems.map((item) => (
                <ChakraLink
                  key={item.path}
                  as={RouterLink}
                  to={item.path}
                  color="gray.300"
                  _hover={{ color: 'white' }}
                  fontSize="sm"
                  fontWeight="medium"
                >
                  <HStack spacing={2}>
                    <Icon as={item.icon} boxSize={4} />
                    <Text>{item.label}</Text>
                  </HStack>
                </ChakraLink>
              ))}
            </HStack>
          )}

          {/* Right side - User menu or Sign in */}
          <HStack spacing={2}>
            {user ? (
              <>
                {/* Desktop User Menu */}
                {!isMobile && (
                  <>
                    <Text
                      fontSize="sm"
                      color="gray.300"
                      textAlign="right"
                      display={{ base: 'none', lg: 'block' }}
                    >
                      Welcome, {user.displayName || user.email}
                    </Text>
                    <Menu>
                      <MenuButton as={Button} rightIcon={<ChevronDownIcon />} variant="ghost" size="sm">
                        <HStack spacing={2}>
                          <Avatar size="sm" name={user.displayName || user.email} />
                          {user.isAdmin && (
                            <Text fontSize="xs" color="green.400" fontWeight="bold">
                              ADMIN
                            </Text>
                          )}
                        </HStack>
                      </MenuButton>
                      <MenuList>
                        <MenuItem
                          icon={<Icon as={FiUser} />}
                          onClick={() => navigate('/profile')}
                        >
                          My Profile
                        </MenuItem>
                        <MenuItem onClick={handleLogout} color="red.400" icon={<Icon as={FiLogOut} />}>
                          Logout
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </>
                )}

                {/* Mobile Hamburger Menu */}
                {isMobile && (
                  <IconButton
                    icon={<HamburgerIcon />}
                    variant="ghost"
                    colorScheme="gray"
                    onClick={onOpen}
                    aria-label="Open navigation menu"
                    size="md"
                  />
                )}
              </>
            ) : (
              <Button
                colorScheme="blue"
                size="sm"
                onClick={handleSignIn}
                px={6}
              >
                Sign In
              </Button>
            )}
          </HStack>
        </Flex>
      </Box>

      {/* Mobile Navigation Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent bg="dark.800" borderLeft="1px" borderColor="dark.700">
          <DrawerCloseButton color="gray.400" />
          <DrawerHeader borderBottomWidth="1px" borderColor="dark.700">
            <HStack spacing={3}>
              <Avatar size="sm" name={user?.displayName || user?.email} />
              <VStack align="start" spacing={0}>
                <Text fontSize="md" color="white" fontWeight="medium">
                  {user?.displayName || user?.email}
                </Text>
                {user?.isAdmin && (
                  <Text fontSize="xs" color="green.400" fontWeight="bold">
                    ADMIN
                  </Text>
                )}
              </VStack>
            </HStack>
          </DrawerHeader>

          <DrawerBody p={4}>
            <VStack spacing={2} align="stretch">
              {/* Admin Link */}
              {user?.isAdmin && (
                <>
                  <MobileNavItem
                    item={{ label: 'Admin', path: '/admin', icon: FiUser }}
                    isAdmin={true}
                  />
                  <Divider borderColor="dark.600" my={2} />
                </>
              )}

              {/* Navigation Items */}
              {navItems.map((item) => (
                <MobileNavItem key={item.path} item={item} />
              ))}

              <Divider borderColor="dark.600" my={2} />

              {/* Profile Link */}
              <MobileNavItem
                item={{ label: 'My Profile', path: '/profile', icon: FiUser }}
              />

              {/* Logout Button */}
              <Button
                leftIcon={<Icon as={FiLogOut} />}
                variant="ghost"
                colorScheme="red"
                justifyContent="flex-start"
                onClick={handleLogout}
                w="full"
                mt={4}
              >
                Logout
              </Button>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
